import { useMutation } from '@tanstack/react-query'
import { CreateProjectPayload } from '@/components/molecules/project-details/CreateProject/useCreateProject.ts'
import { createProjectAndIterationFirebaseFunction } from '@/services/Firebase.ts'
import { ConfigurationTemplate } from '../project-details/CreateProject/types.ts'
import useStore from '@/stores/useStore.js'
import { PromptData } from './schema.ts'

export function useBaseProjectPayload() {
  const organizationsOptions = useStore(state =>
    state.organizations.filter(org => !org.is_reference)
  )

  const organizationId = organizationsOptions[0]?.id
  const teams = useStore(state => state.getTeamsByOrgId(organizationId))

  if (!organizationId || !teams || teams.length === 0) {
    return { organizationId: null, teamId: null }
  }
  const teamId = teams[0].id
  return { organizationId, teamId }
}

export function useCreateProjectMutation(usecaseId: string) {
  const usecase = useUsecase(usecaseId)
  return useMutation({
    mutationFn: async ({
      organizationId,
      teamId,
      assets,
      prompt,
      projectId,
      iterationId,
      usecaseId,
      researchEnabled,
      expandFrontendPrototype,
    }: PromptData & {
      projectId: string
      iterationId: string
      researchEnabled: boolean
      expandFrontendPrototype: boolean
    }) => {
      const projectConfiguration: CreateProjectPayload['formData']['projectConfiguration'] = {
        name: null,
        organizationId,
        prospectName: null,
        prospectWebsite: null,
        techStack: usecaseId,
        teamId,
      }

      return await createProjectAndIterationFirebaseFunction({
        projectId,
        iterationId,
        organizationId,
        teamId,
        projectData: {
          name: null,
          projectConfiguration,
          iterationDefaults: {
            environment: {
              USE_BUSINESS_ANALYST: {
                dontEncrypt: true,
                value: 'true',
              },
              USE_TECHNICAL_ANALYST: {
                dontEncrypt: true,
                value: 'false',
              },
              ...(researchEnabled && {
                RESEARCH_HINTS_ENABLED: {
                  dontEncrypt: true,
                  value: 'true',
                },
                ENABLE_RESEARCH: {
                  dontEncrypt: true,
                  value: 'true',
                },
              }),

              ...(expandFrontendPrototype && {
                EXPAND_FRONTEND_PROTOTYPE: {
                  dontEncrypt: true,
                  value: 'true',
                },
              }),
            },
            repository: {
              githubUsername: null,
            },
            usecase,
          },
        },
        prompt,
        assets,
        dontStartGunslinger: false,
        configuration: projectConfiguration,
      })
    },
  })
}

function useUsecase(usecaseId: string) {
  const usecase = useStore(state =>
    state.getConfigurationTemplateByUsecaseId(usecaseId)
  ) as ConfigurationTemplate

  const useCaseFields = usecase.iterationDefaultsTemplate.usecase?.fields ?? []

  return useCaseFields.reduce(
    (acc, { key, value }) => {
      acc[key] = value
      return acc
    },
    {} as Record<string, string>
  )
}
